/**
 * Maps OncoEMR task thread API payloads to conversation panel models.
 * @module mapOncoemrThreadToTaskConversation
 */

import type {
  OncoemrTaskMessagesResponse,
  OncoemrTaskThreadPdfLink,
  OncoemrThreadMessageEntry,
  OncoemrThreadSenderType,
} from "../../../../../data-model/oncoemrTaskMessagesModel";
import { formatDate } from "../../../../../utils/formatDate";
import {
  resolveMessageSenderDisplayName,
  stripHtmlToPlainText,
} from "../../../../../utils/oncoemrTaskHelpers";
import type {
  TaskConversationData,
  TaskConversationMessage,
} from "../components/tasksConversationPanel";
import type { TaskListPanelItem } from "../components/tasksListPanel";

const TIMESTAMP_FORMAT = "M/D/YY, h:mm A";

/**
 * Builds the header link label for task PDF attachments.
 *
 * @param count - Number of entries in `pdf_links` from the messages API.
 * @returns Label for the PDF viewer trigger, or empty string when there are none.
 */
export const formatTaskPdfLinkLabel = (count: number): string => {
  if (count <= 0) {
    return "";
  }
  if (count === 1) {
    return "View PDF";
  }
  return `View PDFs (${count})`;
};

/**
 * Derives two-letter avatar initials from a display name.
 *
 * @param name - Sender label shown in the thread.
 * @returns Uppercase initials or "?" when empty.
 */
const initialsFromDisplayName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  const first = parts[0][0] ?? "";
  const last = parts[parts.length - 1][0] ?? "";
  return `${first}${last}`.toUpperCase();
};

/**
 * Maps API sender direction to chat bubble alignment.
 *
 * @param senderType - `inbound` vs `outbound` from the thread payload.
 * @returns `left` for inbound traffic and `right` for outbound.
 */
const sideFromSenderType = (
  senderType: OncoemrThreadSenderType,
): TaskConversationMessage["side"] =>
  senderType === "inbound" ? "left" : "right";

/**
 * Converts one thread API row into panel chat props.
 *
 * Sender labels use `sender_name`, then `sender_user_id`, then "—".
 *
 * @param entry - Sent row or outbox-backed placeholder from `GET .../messages`.
 * @returns Bubble model for the task conversation panel.
 */
const mapThreadEntryToConversationMessage = (
  entry: OncoemrThreadMessageEntry,
): TaskConversationMessage => {
  const timestamp = formatDate(entry.sent_at, TIMESTAMP_FORMAT, "—");
  const senderName = resolveMessageSenderDisplayName(entry);
  const side = sideFromSenderType(entry.sender_type);

  if (entry.status === "sent") {
    return {
      id: entry.oncoemr_message_id,
      senderName,
      timestamp,
      message: stripHtmlToPlainText(entry.body),
      side,
      avatarLabel: initialsFromDisplayName(senderName),
    };
  }

  return {
    id: entry.idempotency_key,
    senderName,
    timestamp,
    message: stripHtmlToPlainText(entry.body),
    side,
    avatarLabel: initialsFromDisplayName(senderName),
    deliveryStatus: entry.status,
    idempotencyKey: entry.idempotency_key,
    lastError: entry.status === "failed" ? entry.last_error : undefined,
  };
};

/**
 * Builds header-level conversation state from the left-panel row while messages load.
 *
 * @param panelItem - Selected row from {@link TasksListPanel}.
 * @returns Partial conversation payload with no messages yet.
 */
export const buildPanelBackedConversation = (
  panelItem: TaskListPanelItem,
): TaskConversationData => ({
  taskId: panelItem.id,
  title: panelItem.title,
  status: panelItem.isInProgress ? "In progress" : "Open",
  statusVariant: panelItem.isInProgress ? "inProgress" : "open",
  linkLabel: "",
  pdfLinks: [],
  messages: [],
});

/**
 * Maps a successful messages API response into {@link TaskConversationData}.
 *
 * @param taskRowId - Selected `tasks.id` (path parameter echoed for React keys).
 * @param response - Parsed JSON from `GET .../oncoemr-tasks/{task_row_id}/messages`.
 * @returns Conversation model for the right-hand panel.
 */
export const mapThreadResponseToTaskConversation = (
  taskRowId: string,
  response: OncoemrTaskMessagesResponse,
): TaskConversationData => {
  const { task } = response;
  const statusVariant = task.status === "in_progress" ? "inProgress" : "open";
  const statusLabel = task.status === "in_progress" ? "In progress" : "Open";

  const pdfLinks: OncoemrTaskThreadPdfLink[] = (
    response.pdf_links ?? []
  ).filter((link) => Boolean(link.gcs_path?.trim()));

  return {
    taskId: taskRowId,
    title: task.title,
    status: statusLabel,
    statusVariant,
    linkLabel: formatTaskPdfLinkLabel(pdfLinks.length),
    pdfLinks,
    messages: response.messages.map(mapThreadEntryToConversationMessage),
  };
};
